/**
 * Marin County (California) official results: the County of Marin's food facility inspection data, published on the county's
 * Socrata portal (data.marincounty.gov, dataset 73zb-z5me "Food Facility Inspections"). Read through the shared Socrata CSV sync
 * (services/api/src/official/sync.ts, `format: "socrata_csv"`, parser "marin"); docs/OFFICIAL-RESULTS.md §13.
 *
 * MEASURED 2026-10-06 (one full JSON copy, then about 6 requests, at least 1 s apart; robots.txt asks `Crawl-delay: 1`; /resource/ is
 * not disallowed; 49,759 rows, newest inspection 2026-10-02, data "begins as of January 1, 2014"):
 *  - ONE ROW PER VIOLATION. The row_id is `<business_id>_<inspection_id>_<violation_id>`; an inspection with no violation has one row
 *    with an empty violation_id (row_id ends in "_"). 19,412 inspections at 1,653 businesses. The inspection_id is NOT unique alone
 *    (47 ids belong to two businesses): an inspection is (business_id, inspection_id).
 *  - 46,168 rows carry a violation (code `CHSC 113996`, a California Retail Food Code section: 192 distinct sections, 204 spellings);
 *    7,156 of them have no description; `is_major_violation` is "yes" on 5,268 and empty otherwise; `corrected_on_site` Yes/No.
 *  - 3,364 rows (6.8%) have every text column wrapped in literal double quotes ("NICK THE GREEK" with the quotes inside the value):
 *    the county's own export quirk. We strip one wrapping pair.
 *  - `inspector_comments` is HTML (a template: "Minor violations were also found. For more information, contact Marin County
 *    Environmental Health Services at (415) 473-6907 ..."); 3,531 violation descriptions hold HTML too. We keep the text.
 *  - The result is `inspection_result`/`inspection_description`: NOVIOL "No Violation(s) Found", VIOLMINOR "Only Minor Violation(s)
 *    Found", VIOLFOUND "Violation(s) Found", CASE "Case Opened". The `placard` is GREEN, YELLOW or RED on 45,931 rows ("NA", " " or
 *    empty on the rest: a follow-up visit often issues none: "A new placard was not issued as part of this visit"). No score.
 *  - 28 inspections have rows with two dates and 33 two results (a no-violation row entered before the violations); we take the date
 *    most rows give and, for a placard, only an unambiguous one (2 inspections disagree: no placard shown).
 *  - 936 rows (2 businesses entirely) have no coordinates; there is no phone column. The inspector's name is in the data
 *    (`inspector`, 40 people): it is read only to scrub it from the free text, and never stored or shown.
 *  - The county's own site (marincounty.gov) and its inspection lookup (cdaportal2.marincounty.org, which marinfoodinspection.org
 *    redirects to) answer 403 to our honest request (a Cloudflare bot check): we never fetch them, we only link the lookup.
 *
 * PRIVACY: the violation text and the inspector's comment pass through `scrubComment` (official-scrub.ts): e-mails, phones and likely
 * personal names become "[email]", "[phone]", "[name]". The county's own office number in its boilerplate goes too (one rule). Then
 * every inspector's name the data lists is removed wherever it appears in the text (full name, "S. Bell", the first name after a
 * word like "contact" or "call"). That needs every row first, so the text is scrubbed when the parse is finished (`done`).
 */
import type { OfficialParser, OfficialSource } from "./official-sources.ts";
import { isoDay, tidyName, type FingerprintField, type OfficialCategory, type OfficialInspection, type OfficialPlace, type OfficialPlaceRecord, type OfficialScale, type OfficialViolation } from "./official.ts";
import { SCRUB, scrubComment } from "./official-scrub.ts";

export const MARIN_HOST = "https://data.marincounty.gov";
export const MARIN_DATASET = "73zb-z5me";
export const MARIN_DATA_URL = `${MARIN_HOST}/resource/${MARIN_DATASET}.csv`;
export const MARIN_METADATA_URL = `${MARIN_HOST}/api/views/${MARIN_DATASET}.json`;
export const MARIN_DATASET_PAGE = `${MARIN_HOST}/d/${MARIN_DATASET}`;
/** The county's lookup (its research file's link; the dataset's own page names marinfoodinspection.org, a 301 to this). Linked, never fetched. */
export const MARIN_LOOKUP = "https://cdaportal2.marincounty.org/FoodInspectionSearch";
export const MARIN_SCALE_ID = "CA/marin:placard";

/**
 * `inspector` is asked for ONE reason: to scrub the county staff's names out of the free text (MEASURED 2026-10-06: "call Shannon Bell at
 * 415-...", "submit copies to Eithne Bullick", "Inspectors S. Bell and O. Duque-Turcios were present" are in violation text). It is held
 * in memory for the run and never stored, shown or logged. The correct-by date is not read (it is often before the inspection).
 */
export const MARIN_SELECT = "business_id,business_name,license_number,inspection_id,inspection_date,inspection_type,inspection_description,inspection_result,placard,inspector,inspector_comments,"
  + "corrected_on_site,violation_id,violation_code,violation_description,is_major_violation,formatted_address,business_city,business_postal_code,businessaddress,row_id";
export const MARIN_FINGERPRINT: FingerprintField[] = [
  { name: "business_id", type: "text", required: true }, { name: "business_name", type: "text" }, { name: "license_number", type: "text" },
  { name: "inspection_id", type: "text", required: true }, { name: "inspection_date", type: "date", required: true }, { name: "inspection_type", type: "text" },
  { name: "inspection_description", type: "text" }, { name: "inspection_result", type: "text" }, { name: "placard", type: "text" }, { name: "inspector", type: "text" }, { name: "inspector_comments", type: "text" },
  { name: "corrected_on_site", type: "text" }, { name: "violation_id", type: "text" }, { name: "violation_code", type: "text" }, { name: "violation_description", type: "text" },
  { name: "is_major_violation", type: "text" }, { name: "formatted_address", type: "text" }, { name: "business_city", type: "text" }, { name: "business_postal_code", type: "text" },
  { name: "businessaddress", type: "text" }, { name: "row_id", type: "text", required: true },
];

export const MARIN_SOURCES: readonly OfficialSource[] = [
  {
    // Names from the pack's node (CA/marin: "Marin County Community Development Agency, Environmental Health Services", Marin County).
    id: "marin", state: "CA", jurisdiction: "CA/marin", department: "", area: "", status: "sync",
    format: "socrata_csv", urls: [MARIN_DATA_URL],
    // Every inspection the county publishes (it starts 2014): a window far wider than the data, never a cut.
    socrata: { select: MARIN_SELECT, dateField: "inspection_date", order: "row_id" }, pageSize: 10_000, years: 60, conditional: "last_modified",
    fingerprint: MARIN_FINGERPRINT, parser: "marin", lazyColumns: ["inspector_comments", "violation_description"], categories: true,
    updates: "daily by the county (we check monthly)",
    // Shown (owner, 2026-10-05: show all counties) once the tests pass.
    displayApproved: true,
    basis: {
      kind: "facts",
      terms: "County of Marin open data under the Open Data Commons Open Database License (attribution: County of Marin, CA)",
      // The metadata's own license name (services/api/test/fixtures/marin/metadata.json).
      quote: "Open Data Commons Open Database License", url: MARIN_METADATA_URL,
    },
    flag: { clause: "Open Data Commons Open Database License: attribution and share-alike apply", url: "https://opendatacommons.org/licenses/odbl/1.0/" },
    attribution: "County of Marin, CA, Community Development Agency, Environmental Health Services, data.marincounty.gov",
    lookupUrl: MARIN_LOOKUP,
    recordHint: "Search the county's inspection lookup for the name or the license number.",
    howOften: { text: "In the county's data, most inspections are labeled semi-yearly, then yearly; a follow-up visit comes after violations.", url: MARIN_DATASET_PAGE },
    scaleLine: {
      line: "Each inspection ends with a green, yellow or red placard, and the county gives no score. In the county's data, inspectors write \"Green (Pass) placard posted\" and that a yellow placard is posted when multiple major violations are observed. Ask the department what each color needs.",
      citation: "County of Marin, Food Facility Inspections (inspector_comments, read 2026-10-06)",
      url: MARIN_DATASET_PAGE,
    },
  },
];

export const MARIN_SCALE: OfficialScale = {
  id: MARIN_SCALE_ID, kind: "pass_fail", unit: "", noun: "inspection result", lowerIsBetter: false, min: 0, max: 0,
  meaning: "Marin County posts a green, yellow or red placard after an inspection, and gives no score. In the county's own inspection data, inspectors write \"Green (Pass) placard posted\" and \"a yellow placard is posted due to the multiple major violations observed\". We could not read a county page that lists what each color needs, so ask the department.",
  source: {
    citation: "County of Marin, Food Facility Inspections: inspector_comments, \"Green (Pass) placard posted\" and \"a yellow placard is posted due to the multiple major violations observed\" (read 2026-10-06)",
    url: MARIN_DATASET_PAGE,
  },
};

/** The county's placard words → ours. Anything else ("NA", blank, empty) is no placard. */
export const MARIN_PLACARD: Readonly<Record<string, string>> = { GREEN: "Green", YELLOW: "Yellow", RED: "Red" };

/** The data names no kind of place: only what the name says (a restaurant stays "other", so typical ranges use all food places). */
export function marinCategory(name: string): OfficialCategory {
  if (/\b(coffee|espresso)\b/i.test(name)) return "coffee";
  if (/\b(market|grocery|supermarket)\b/i.test(name)) return "market";
  return "other";
}
/** A microenterprise home kitchen is someone's home: city only (none seen in the 2026-10-06 data; a guard, by the name). */
const HOME_KITCHEN = /\bmehko\b|micro ?enterprise home kitchen/i;

// ── Codes → the five risk factors ─────────────────────────────────────────────────────────────────────────────────

/** "CHSC 113953.c" → "113953" (a trailing letter is a subdivision of the section); "CHSC 114259.1" → "114259.1" (a digit is another section). "" for anything else. */
export function marinBase(code: string): string {
  const m = /^(?:CHSC\s+)?(\d+(?:\.\d+)?)(?:\.[A-Za-z](?:-[A-Za-z])?)?(?:\([^)]*\))?\s*$/i.exec(code.trim());
  return m ? m[1]! : "";
}
/**
 * Marin's violation codes are California Retail Food Code (CalCode, Health and Safety Code) sections. The code → FDA Form 3-A item
 * table is DERIVED 2026-10-06 from the inspector crosswalk already in core (states/ca/inspector.ts: each Los Angeles County FOIR
 * category cites its CalCode sections and carries its FDA items): a section cited by one or more categories maps to those categories'
 * items (the union) when all of them land on the same set of the five risk factors; a section that two categories split (113953:
 * handwashing sinks vs hot water), or whose category has no FDA item, or that no category cites, is NOT mapped (never "clear").
 * Pinned in services/api/test/marin.test.ts: re-derived from the crosswalk, and the coverage on the 2026-10-06 data
 * (85 of 192 sections, 34,761 of 46,168 violation rows = 75.3%).
 */
export const MARIN_CODE_FDA: Readonly<Record<string, readonly number[]>> = {
  "113945": [1], "113947": [1], "113948": [2], "113950": [3, 4, 5], "113952": [8, 9, 46], "113967": [13], "113969": [40], "113971": [40], "113976": [13], "113977": [6],
  "113980": [11, 12, 13], "113984": [15, 16, 39], "113986": [15, 39], "113992": [42], "113996": [21, 22], "113998": [21, 22], "114000": [24], "114002": [20, 33],
  "114004": [18], "114010": [18], "114012": [25], "114016": [19], "114018": [35], "114020": [35], "114021": [11, 12], "114023": [11, 12], "114024": [11, 12],
  "114025": [11, 12], "114027": [11, 12], "114037": [21, 22], "114039": [14], "114047": [37, 39], "114049": [37, 39], "114051": [37, 39], "114057": [29, 32],
  "114079": [17], "114081": [43, 44, 45], "114087": [37], "114089": [37], "114091": [26], "114093": [25], "114097": [16], "114101": [48], "114115": [49],
  "114119": [43, 44, 45], "114130": [33, 47], "114149": [56], "114157": [36], "114159": [36], "114175": [33, 47, 49], "114190": [51], "114192": [50, 51],
  "114193": [51], "114197": [52], "114244": [54], "114245": [54], "114250": [52, 53], "114252": [56], "114254": [28], "114256": [38], "114257": [55],
  "114266": [55], "114271": [55], "114276": [53], "113945.1": [1], "113947.1": [2], "113949.5": [3, 4, 5], "113953.1": [10], "113953.2": [10], "113953.3": [8, 9, 46],
  "113953.4": [8, 9, 46], "113984.1": [1], "114002.1": [20, 33], "114039.1": [14], "114039.2": [14], "114039.3": [14], "114039.4": [14], "114039.5": [14],
  "114185.1": [41], "114254.1": [28], "114254.2": [28], "114256.1": [38], "114259.1": [38], "114259.4": [38], "114259.5": [38],
};

// ── Text: the county's HTML and its quote quirk ───────────────────────────────────────────────────────────────────

/** One pair of literal double quotes wrapped around a whole value (the county's export quirk) is removed. */
export const unwrapQuotes = (v: string | undefined): string => { const t = (v ?? "").trim(); return t.length > 1 && t.startsWith('"') && t.endsWith('"') ? t.slice(1, -1).trim() : t; };
const ENTITIES: Record<string, string> = { amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", nbsp: " ", ndash: "-", mdash: "-", rsquo: "'", lsquo: "'", ldquo: '"', rdquo: '"', deg: "°" };
const decodeEntities = (s: string) => s.replace(/&(?:#(\d{1,6})|#x([0-9a-f]{1,5})|([a-z]{2,8}));/gi, (m, dec: string | undefined, hex: string | undefined, name: string | undefined) => {
  if (name) return ENTITIES[name.toLowerCase()] ?? m;
  const n = dec ? Number(dec) : parseInt(hex!, 16);
  return n > 0 && n < 0x110000 ? String.fromCodePoint(n) : m;
});
/** The text of the county's HTML (tags dropped, entities decoded, one space between words). A "<" that does not start a tag ("<41F") stays. */
export function htmlText(raw: string): string {
  const t = raw.replace(/<\/?(?:br|p|div|li|tr|h\d)\b[^>]*>/gi, " ").replace(/<\/?[a-zA-Z][^>]*>/g, " ");
  return decodeEntities(t).replace(/[ \t\r\n ]+/g, " ").trim();
}

// ── The parser ─────────────────────────────────────────────────────────────────────────────────────────────────────

/** An inspector's name as the county writes it ("JENNIFER SNOW", or "S. BELL"): words, tidied. */
const nameWords = (raw: string) => unwrapQuotes(raw).replace(/[^A-Za-z.'\- ]+/g, " ").split(/\s+/).filter(Boolean);
const escRe = (x: string) => x.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
/** A scrubber for the county staff named in `inspector`: whole name, "S. Bell", and the first name right after a directing word. */
export function inspectorScrubber(inspectors: Iterable<string>): (text: string) => { text: string; hits: number } {
  const full: string[] = [], initials: string[] = [], first = new Set<string>();
  for (const raw of inspectors) {
    const w = nameWords(raw);
    if (w.length >= 2) {
      const [f, l] = [w[0]!, w.slice(1).join(" ")];
      full.push(`${escRe(f)}[\\s.]+${escRe(l).replace(/ /g, "[\\s.]+")}`);
      if (f.length > 1) initials.push(`\\b${escRe(f[0]!)}\\.\\s*${escRe(l).replace(/ /g, "[\\s.]+")}`);
      if (f.length >= 3) first.add(f[0]!.toUpperCase() + f.slice(1).toLowerCase());
    }
  }
  const fullRe = full.length ? new RegExp(`(?<![A-Za-z])(?:${full.join("|")})(?![A-Za-z])`, "gi") : null;
  const initRe = initials.length ? new RegExp(`(?:${initials.join("|")})(?![A-Za-z])`, "gi") : null;
  const firstRe = first.size ? new RegExp(`\\b((?:contact|call|calls|to|attn:?|attention|email|e-mail|fax|ask for|see|notify|inform|text)\\s+)(${[...first].map(escRe).join("|")})(?![A-Za-z])`, "gi") : null;
  return (text) => {
    let hits = 0;
    let t = fullRe ? text.replace(fullRe, () => { hits++; return SCRUB.name; }) : text;
    if (initRe) t = t.replace(initRe, () => { hits++; return SCRUB.name; });
    if (firstRe) t = t.replace(firstRe, (m, lead: string, name: string) => { if (!/^[A-Z]/.test(name)) return m; hits++; return `${lead}${SCRUB.name}`; });
    return { text: t, hits };
  };
}

type Row = Record<string, string>;
export interface MarinStats { rows: number; businesses: number; inspections: number; violations: number; inspectionsWithoutRows: number; commentsFlagged: number; names: number; emails: number; phones: number; inspectorNames: number; noName: number; badDates: number; dateConflicts: number; placardConflicts: number; }
interface InspDraft {
  placeKey: string; id: string; dates: Map<string, number>; placards: Set<string>; results: Map<string, number>; type: string;
  note: string | null; violations: OfficialViolation[]; codes: string[]; rowIds: Set<string>;
}
interface Draft { place: OfficialPlace; lastDate: string; license: string; inspections: Map<string, InspDraft> }

const mostCommon = (m: Map<string, number>): string => { let best = "", n = 0; for (const [k, c] of [...m].sort((a, b) => (a[0] < b[0] ? -1 : 1))) if (c > n) { best = k; n = c; } return best; };
const num = (v: string | undefined): number | null => (v === undefined || v.trim() === "" || !Number.isFinite(Number(v)) ? null : Number(v));
const compact = (s: string) => s.replace(/\s+/g, " ").trim();

/** "75 BELLAM BLVD\nSAN RAFAEL, CA 94901\n(37.95967, -122.50649)" → the street and the coordinates (the county's location column). */
export function marinLocation(raw: string): { street: string; geo: { lat: number; lon: number } | null } {
  const lines = raw.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const g = /\(\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*\)\s*$/.exec(lines.at(-1) ?? "");
  const lat = g ? Number(g[1]) : NaN, lon = g ? Number(g[2]) : NaN;
  const geo = g && Number.isFinite(lat) && Number.isFinite(lon) && lat !== 0 && lon !== 0 ? { lat, lon } : null;
  return { street: lines.length > 1 ? lines[0]! : "", geo };
}
/** "34 MAIN ST, TIBURON, CA 94920" → "34 MAIN ST" when the place's own street is not in the location column. */
const streetOfFormatted = (f: string, city: string) => {
  const t = f.replace(/,\s*CA\s+\d{5}(?:-\d{4})?\s*$/i, "");
  const c = city ? t.lastIndexOf(`, ${city}`) : -1;
  return compact(c > 0 ? t.slice(0, c) : t);
};

/**
 * Marin: one row per violation; the inspection is (business_id, inspection_id). Every inspection and every violation is kept (no
 * cap). `src` is the registry entry with its names filled from the pack. Full-records model (fix/full-records): the violation text is never trimmed
 * (the inspector's line for the visit stays whole), and Marin gives no title for a code, so `codeFieldsFor("marin")` is empty and nothing goes to the code book.
 */
export function makeMarin(src: OfficialSource, fetchedAt: string, hash: string): OfficialParser & { stats: MarinStats } {
  const stats: MarinStats = { rows: 0, businesses: 0, inspections: 0, violations: 0, inspectionsWithoutRows: 0, commentsFlagged: 0, names: 0, emails: 0, phones: 0, inspectorNames: 0, noName: 0, badDates: 0, dateConflicts: 0, placardConflicts: 0 };
  const drafts = new Map<string, Draft>();
  // The inspectors the data names (held for the run, never stored): their names are removed from the free text when the parse is done.
  const inspectors = new Set<string>();
  const add = (rows: readonly Row[]) => { for (const r of rows) {
    const biz = (r.business_id ?? "").trim(), insp = (r.inspection_id ?? "").trim();
    const date = isoDay(r.inspection_date ?? "");
    if (!biz || !insp) continue;
    if (!date) { stats.badDates++; continue; }
    stats.rows++;
    const key = `${src.id}:${biz}`;
    let d = drafts.get(key);
    const name = tidyName(unwrapQuotes(r.business_name));
    const city = tidyName(unwrapQuotes(r.business_city));
    const loc = marinLocation(r.businessaddress ?? "");
    const street = tidyName(loc.street || streetOfFormatted(unwrapQuotes(r.formatted_address), unwrapQuotes(r.business_city)));
    const zip = (r.business_postal_code ?? "").trim().slice(0, 5);
    const license = unwrapQuotes(r.license_number);
    if (!d) {
      const place: OfficialPlace = {
        key, sourceId: src.id, jurisdiction: src.jurisdiction, area: src.area, extId: biz, name, address: street, city, zip,
        ...(loc.geo ? { geo: loc.geo, geoSource: "source" as const } : {}), sourceCategory: "", category: marinCategory(name),
        recordUrl: src.lookupUrl, recordHint: src.recordHint, department: src.department,
        dataUrl: `${MARIN_HOST}/resource/${MARIN_DATASET}.json?business_id=${encodeURIComponent(biz)}`,
      };
      d = { place, lastDate: date, license, inspections: new Map() };
      drafts.set(key, d);
      stats.businesses++;
    } else if (date >= d.lastDate) {
      // The newest inspection's spelling wins (a place renamed or moved); an empty value never wipes a held one.
      d.lastDate = date;
      if (name) { d.place.name = name; d.place.category = marinCategory(name); }
      if (street) d.place.address = street;
      if (city) d.place.city = city;
      if (zip) d.place.zip = zip;
      if (license) d.license = license;
      if (loc.geo) { d.place.geo = loc.geo; d.place.geoSource = "source"; }
    } else if (!d.place.geo && loc.geo) { d.place.geo = loc.geo; d.place.geoSource = "source"; }
    if (!d.place.name && name) { d.place.name = name; d.place.category = marinCategory(name); }
    let i = d.inspections.get(insp);
    if (!i) {
      i = { placeKey: key, id: insp, dates: new Map(), placards: new Set(), results: new Map(), type: "", note: null, violations: [], codes: [], rowIds: new Set() };
      d.inspections.set(insp, i);
    }
    const rowId = (r.row_id ?? "").trim();
    if (rowId) { if (i.rowIds.has(rowId)) continue; i.rowIds.add(rowId); }
    i.dates.set(date, (i.dates.get(date) ?? 0) + 1);
    const placard = unwrapQuotes(r.placard).toUpperCase();
    if (MARIN_PLACARD[placard]) i.placards.add(placard);
    const result = unwrapQuotes(r.inspection_description);
    if (result) i.results.set(result, (i.results.get(result) ?? 0) + 1);
    if (!i.type) i.type = tidyName(unwrapQuotes(r.inspection_type));
    // The inspection's own comment repeats on every row: scrub it once.
    const who = unwrapQuotes(r.inspector); if (who) inspectors.add(who);
    if (i.note === null && r.inspector_comments) { const t = htmlText(unwrapQuotes(r.inspector_comments)); if (t) i.note = t; }
    const code = compact(unwrapQuotes(r.violation_code));
    if (code) {
      stats.violations++;
      const text = r.violation_description ? htmlText(unwrapQuotes(r.violation_description)) : "";
      const v: OfficialViolation = { code, text, critical: /^yes$/i.test(unwrapQuotes(r.is_major_violation)), ...(/^yes$/i.test(unwrapQuotes(r.corrected_on_site)) ? { cos: true } : {}) };
      i.violations.push(v); i.codes.push(code);
    }
  } };
  const done = (): OfficialPlaceRecord[] => {
    // The free text, scrubbed now that every inspector is known: e-mails, phones, likely names (the SCC rule), then the staff's own names.
    const staff = inspectorScrubber(inspectors);
    const cache = new Map<string, { text: string; flagged: boolean; names: number; emails: number; phones: number; staff: number }>();
    const scrub = (raw: string): string => {
      let c = cache.get(raw);
      if (!c) { const s = scrubComment(raw); const t = staff(s.text); c = { text: t.text.trim(), flagged: s.flagged, names: s.names, emails: s.emails, phones: s.phones, staff: t.hits }; cache.set(raw, c); }
      if (c.flagged) stats.commentsFlagged++;
      stats.names += c.names; stats.emails += c.emails; stats.phones += c.phones; stats.inspectorNames += c.staff;
      return c.text;
    };
    const out: OfficialPlaceRecord[] = [];
    for (const d of drafts.values()) {
      if (!d.place.name) { stats.noName++; continue; }
      if (HOME_KITCHEN.test(d.place.name)) { d.place.address = ""; d.place.zip = ""; delete d.place.geo; delete d.place.geoSource; d.place.privacy = "city_only"; }
      const inspections: OfficialInspection[] = [];
      for (const i of d.inspections.values()) {
        if (i.dates.size > 1) stats.dateConflicts++;
        if (i.placards.size > 1) stats.placardConflicts++;
        for (const v of i.violations) if (v.text) v.text = scrub(v.text);
        const note = i.note ? scrub(i.note) : "";
        const date = mostCommon(i.dates);
        const placard = i.placards.size === 1 ? [...i.placards][0]! : "";
        const result = mostCommon(i.results);
        const critical = i.violations.filter((v) => v.critical).length;
        // The county says violations were found but lists none (58 + 21 rows in the 2026-10-06 data): the list is unknown, never "none".
        const said = /violation/i.test(result) && !/^no violation/i.test(result);
        const insp: OfficialInspection = {
          placeKey: i.placeKey, date, score: null, grade: placard ? `${MARIN_PLACARD[placard]} placard` : null, result: result || null, scaleId: MARIN_SCALE_ID,
          type: i.type || null, routine: /^routine$/i.test(i.type),
          violations: { count: i.violations.length, critical, items: i.violations, ...(i.codes.length ? { codes: i.codes } : {}), ...(said && !i.violations.length ? { partial: true } : {}) },
          ...(note ? { note } : {}), recordUrl: d.place.recordUrl,
        };
        inspections.push(insp);
        stats.inspections++;
      }
      if (!inspections.length) { stats.inspectionsWithoutRows++; continue; }
      inspections.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
      if (d.license) d.place.recordHint = `Search the county's inspection lookup for the name or license number ${d.license}.`;
      out.push({ place: d.place, inspections, provenance: { sourceId: src.id, fetchedAt, hash } });
    }
    return out.sort((a, b) => (a.place.key < b.place.key ? -1 : 1));
  };
  return { add, done, stats };
}
